import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Segment, Table, TableProps } from 'tea-component';
import { getFormatNumPlus } from '@gp/utils/common';
import { getTableList, getColumns } from './utils';
import { COMMON_DEFAULT_VALUE } from '../../constants';
import {
  SEGMENT_OPT,
  SEGMENT_GROSS_REVENUE,
  SEGMENT_NET_PROFIT,
  RECORDS_MAP,
} from './constants';
import { fetchStudioOverallFinanceForecastListData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { useRequest } from 'ahooks';
import classNames from 'classnames';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import { formatStudioValues } from '@gp/pages/StudioDashboard/StudioOverall/utils';

const { autotip, injectable, mergeable, scrollable } = Table.addons;

interface BreakdownListPropsType {
  filterBOLoading: boolean;
  selectBO: string[];
  selectValue: {
    type: 'market' | 'region';
    regions: string[];
    markets: string[];
  };
  /**
   * 上方卡片全部无数据显示No Data时，卡片高度变了，这时表格第一行粘性定位的容器高度也要调整
   * 但其实这个时候下方也就显示一行total，滚动不了，触发屏幕很小
   */
  isScenarioCardAllNoData: boolean;
}
type CommonType = {
  grossRevenue: any;
  netProfit: any;
};
type IRecords = {
  uniqueId: string;
  boName: string;
  isSpacerBlock: boolean;
  isAllTotal: boolean;
  isTotal: boolean;
  studioId: string;
  studioName: string;
  studioCover: string;
  // '[year_base]': string,
  // '[year_conservative]': string,
  [keyName: string]: any;
};

const DEFAULT_PROPS: BreakdownListPropsType = {
  filterBOLoading: false,
  selectBO: [],
  selectValue: {
    type: 'region',
    regions: [],
    markets: [],
  },
  isScenarioCardAllNoData: false,
};

function BreakdownList(props: BreakdownListPropsType) {
  const { filterBOLoading, selectBO, selectValue, isScenarioCardAllNoData } = props;

  const [segmentValue, setSegmentValue] = useState<'1' | '2'>(
    SEGMENT_GROSS_REVENUE,
  );
  const [records, setRecords] = useState<IRecords[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [tableVirtualLoading, setTableVirtualLoading] =
    useState<boolean>(false);
  // 列表第一行Portfolio Total的数据
  const [firstAllTotalList, setFirstAllTotalList] =
    useState<CommonType>(COMMON_DEFAULT_VALUE);
  // 表格是否出现横向滚动条
  // const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);
  // const [scrollTop, setScrollTop] = useState<number>(0);
  // const firstRowContainerRef = useRef<HTMLElement>(null);

  // 列表接口
  const {
    loading: listLoading,
    data: data,
    run: run,
  } = useRequest(fetchStudioOverallFinanceForecastListData, { manual: true });

  const loading = useMemo(() => {
    return filterBOLoading || listLoading || tableVirtualLoading;
  }, [filterBOLoading, listLoading, tableVirtualLoading]);

  // 组织数据
  const orgList = useMemo(() => {
    const yearsData: {
      grossRevenue: string[];
      netProfit: string[];
    } = {
      grossRevenue: [],
      netProfit: [],
    };
    const recordsData: {
      grossRevenue: any[];
      netProfit: any[];
    } = {
      grossRevenue: [],
      netProfit: [],
    };
    if (loading) return { yearsData, recordsData };
    const segmentValueKey = RECORDS_MAP[segmentValue];
    const { years, gross_revenue: grossRevenueList, net_profit: netProfitList } = data || {};
    const { gross_revenue: grossRevenueYears, net_profit: netProfitYears } = years || {};
    yearsData.grossRevenue = Array.isArray(grossRevenueYears) ? grossRevenueYears : [];
    yearsData.netProfit = Array.isArray(netProfitYears) ? netProfitYears : [];
    const segmentValueYears = yearsData[segmentValueKey] || [];
    // @ts-ignore
    recordsData.grossRevenue = getTableList(grossRevenueList || null, segmentValueYears);
    // @ts-ignore
    recordsData.netProfit = getTableList(netProfitList || null, segmentValueYears);
    if (!selectBO?.length) {
      setYears(segmentValueYears);
      setFirstAllTotalList({
        grossRevenue: recordsData.grossRevenue?.[0],
        netProfit: recordsData.netProfit?.[0],
      });
    }
    setRecords(recordsData[segmentValueKey] || []);
    return { yearsData, recordsData };
  }, [loading, selectBO]);

  const firstLineList = useMemo(() => {
    return firstAllTotalList?.[RECORDS_MAP?.[segmentValue]];
  }, [firstAllTotalList, segmentValue]);

  // 第一行固定的内容是否显示
  const isShowFirstRow = useMemo(() => {
    return !loading && !tableVirtualLoading && firstLineList;
  }, [loading, tableVirtualLoading, firstLineList]);
  // 根据数据 计算合并单元格情况
  const [rowGroupNumList, setRowGroupNumList] = useState<Array<number>>([]);
  useEffect(() => {
    if (loading || tableVirtualLoading) return;
    if (Array.isArray(records) && records?.length) {
      let tempList = [];
      let preRowId = 0,
        rowNum = 0;
      for (let i = 0; i < records?.length; i++) {
        const t = records?.[i];
        if (t?.isTotal) {
          rowNum = i - preRowId;
          preRowId = i;
          tempList.push(rowNum);
        }
      }
      tempList.push(records?.length - preRowId);
      tempList.shift();
      setRowGroupNumList(tempList);
    }
  }, [loading, tableVirtualLoading, records]);

  let groupIndex = 0;
  const [isColShadow, setColShadow] = useState(false);

  // 根据年份的列数计算表格的宽度
  const totalWidth = useMemo(() => {
    return 150 + 200 + 150 + 130 * years?.length;
  }, [years?.length]);

  const addons = [
    scrollable({
      minWidth: totalWidth,
      // maxHeight: 500,
    }),
    injectable({
      cell: (props, context) => ({
        className: `${
          (context.rowKey === '0' && context.columnIndex !== 0) ||
          (context.columnIndex == 0 && context.recordIndex == 0)
            ? 'table-highlight-cell'
            : ''
        }${
          // table fixed属性和动态计算合并单元格冲突，此处手动模拟。阴影after沿用tea自己的样式类名
          isColShadow
            ? context.columnIndex == 1
              ? ' table-col--fixed table-col--fixed-left'
              : context.columnIndex == 0
              ? ' table-col--fixed'
              : ''
            : ''
        }`,
      }),
      row: (props, context) => ({
        className:
          context.rowKey === '0' && context.recordIndex !== 0
            ? 'table-group-row'
            : '',
      }),
    }),
    mergeable({
      rowSpan: (columnIndex, recordIndex, context) => {
        if (rowGroupNumList.length === 0) {
          return 1;
        }
        if (columnIndex === 0) {
          if (context.rowKey === '0' && groupIndex < rowGroupNumList.length) {
            groupIndex++;
            return rowGroupNumList[groupIndex - 1];
          }
          return 0;
        }
        return 1;
      },
    }),
    autotip({
      isLoading: loading,
      hideIcon: !loading, // loading状态显示图标，其他情况不显示
      emptyText: 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  const handleScroll = useCallback((e) => {
    // 表体横向滚动时，第一行固定的内容同步滚动
    // const scrollLeft = e?.target?.scrollLeft;
    // if (firstRowContainerRef?.current) {
    //   firstRowContainerRef.current.scrollLeft = scrollLeft;
    // }
    // table fixed属性和动态计算合并单元格冲突，此处手动模拟
    const scrollLeft = e?.target?.scrollLeft;
    if (scrollLeft > 0) {
      setColShadow(true);
    } else {
      setColShadow(false);
    }
  }, []);

  // 最外层容器纵向滚动时
  // const handleOverallBodyScroll = useCallback((e) => {
  //   const scrollTop = e?.target?.scrollTop;
  //   setScrollTop(scrollTop);
  // }, []);

  // 请求页面整体数据
  useEffect(() => {
    Array.isArray(selectBO) && run({ management_team: selectBO, data_types: formatStudioValues(selectValue) });
  }, [selectBO, selectValue]);

  useEffect(() => {
    const { yearsData, recordsData } = orgList || {};
    const segmentValueKey = RECORDS_MAP[segmentValue];
    setYears(yearsData?.[segmentValueKey] || []);
    setRecords(recordsData?.[segmentValueKey] || []);
    // 虚拟loading
    const t = setTimeout(() => {
      setTableVirtualLoading(false);
      clearTimeout(t);
    }, 100);
  }, [segmentValue]);

  // 为表格添加遮挡间隙边框的dom
  // useEffect(() => {
  //   if (loading) return;
  //   if (Array.isArray(records) && records?.length) {
  //     const tableEl = document.querySelector('.dbg-studio-overall-finance-year-forecast-list-table');
  //     if (tableEl) {
  //       let spacerBlockNum = 0, studioLine = 0;
  //       for (let i = 0; i < records?.length; i++) {
  //         const t = records?.[i];
  //         t?.isTotal || studioLine++;
  //         if (t?.isSpacerBlock) {
  //           const shadeTop = ((i + 1) * 105 + spacerBlockNum * 20 - studioLine + 46) + 'px';
  //           const leftShade = document.createElement('span');
  //           leftShade.className = 'gp-table-border-shade';
  //           leftShade.style.left = '0px';
  //           leftShade.style.top = shadeTop;
  //           const rightShade = document.createElement('span');
  //           rightShade.className = 'gp-table-border-shade';
  //           rightShade.style.right = '0px';
  //           rightShade.style.top = shadeTop;
  //           tableEl.appendChild(leftShade);
  //           tableEl.appendChild(rightShade);
  //           spacerBlockNum++;
  //         }
  //       }
  //     }
  //   }
  // }, [loading, records]);

  // // 把遮挡边框的dom删除
  // const deleteEmptyBorder = useCallback(() => {
  //   const shadeEls = document.querySelectorAll('.dbg-studio-overall-finance-year-forecast-list-table .gp-table-border-shade');
  //   for (let i = 0; i < shadeEls?.length; i++) {
  //     const shadeEl = shadeEls?.[i];
  //     if (shadeEl) {
  //       shadeEl?.remove();
  //     }
  //   }
  // }, []);

  // // loading时把遮挡边框的dom删除
  // useEffect(() => {
  //   if (loading) {
  //     deleteEmptyBorder();
  //   }
  // }, [loading]);

  // 添加滚动监听
  useEffect(() => {
    // 表体滚动监听，滚动时第一行固定的内容也滚动
    const t = setInterval(() => {
      const tBody = document.querySelector(
        '.dbg-studio-overall-finance-year-forecast-list-table .tea-table__body',
      );
      if (tBody) {
        tBody?.addEventListener('scroll', handleScroll);
        clearInterval(t);
      }
    }, 100);
    // // 最外层容器滚动添加监听，用于表格第一行粘性定位加容器高度
    // const tt = setInterval(() => {
    //   const overallBody = document.querySelector('.dbg-studio-overall-body');
    //   if (overallBody) {
    //     overallBody?.addEventListener('scroll', handleOverallBodyScroll);
    //     clearInterval(tt);
    //   }
    // }, 100);

    return () => {
      const tBody = document.querySelector(
        '.dbg-studio-overall-finance-year-forecast-list-table .tea-table__body',
      );
      if (tBody) {
        tBody?.removeEventListener('scroll', handleScroll);
      }
      // const overallBody = document.querySelector('.dbg-studio-overall-body');
      // if (overallBody) {
      //   overallBody?.removeEventListener('scroll', handleOverallBodyScroll);
      // }
    };
  }, []);

  return (
    <CardSlot
      className="dbg-studio-overall-finance-year-forecast-list-container"
      title="Breakdown"
    >
      {/* <h3 className="dbg-studio-overall-finance-year-forecast-list-title">
        Breakdown
      </h3> */}
      <Segment
        className="dbg-studio-overall-finance-year-forecast-list-segment segment-dark"
        value={segmentValue}
        options={SEGMENT_OPT}
        onChange={(value: '1' | '2') => {
          setRecords([]);
          setTableVirtualLoading(true); // 虚拟loading
          setSegmentValue(value);
          // 把遮挡边框的dom删除
          // deleteEmptyBorder();
        }}
      />
      <Table
        className={classNames({
          'dbg-common-table dbg-studio-overall-finance-year-forecast-list-table dbg-table-sticky dbh-action-state-center-table':
            true,
          'tbody-bottom-border': loading || !records?.length,
          'theader-bottom-border': loading || !records?.length,
        })}
        records={
          loading
            ? []
            : records?.length
            ? records
            : firstLineList
            ? [firstLineList]
            : []
        }
        recordKey="uniqueId"
        columns={
          getColumns(
            segmentValue,
            loading ? [] : years,
          ) as TableProps['columns']
        }
        addons={addons}
      />
      {/* 第一行表格内容，为了适配粘性定位 */}
      {
        // /* isShowFirstRow */false && (
        //   <div
        //     className="dbg-studio-overall-finance-year-forecast-list-first-row"
        //     // 触发粘性定位零界点时，需要给容器加高度，不然粘性定位不生效，高度不够还是会滚动走
        //     // 现在改成动态改变top值，这样不会遮挡表头内容
        //     style={{
        //       // height: scrollTop > (isScenarioCardAllNoData ? 486 : 612) ? (scrollTop - (isScenarioCardAllNoData ? 486 : 612) + 106) : '',
        //       top: scrollTop > (isScenarioCardAllNoData ? 491 : 617) ? (scrollTop - (isScenarioCardAllNoData ? 491 : 617) + 164) : 164,
        //     }}
        //   >
        //     <div ref={firstRowContainerRef} className="dbg-studio-overall-finance-year-forecast-list-first-row-container">
        //       <ul className="dbg-studio-overall-finance-year-forecast-list-first-row-body">
        //         {/* bo */}
        //         <li
        //           className="dbg-studio-overall-finance-year-forecast-list-first-row-item"
        //           style={{
        //             width: 140 / totalWidth * 100 + '%',
        //             minWidth: '140px',
        //           }}
        //         >
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //             style={{
        //               justifyContent: 'flex-start',
        //             }}
        //             title={firstLineList?.boName || '-'}
        //           >{firstLineList?.boName || '-'}</div>
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //           ></div>
        //         </li>
        //         {/* studio */}
        //         <li
        //           className="dbg-studio-overall-finance-year-forecast-list-first-row-item"
        //           style={{
        //             width: 200 / totalWidth * 100 + '%',
        //             minWidth: '200px',
        //           }}
        //         >
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //             style={{
        //               justifyContent: 'flex-start',
        //             }}
        //             title={firstLineList?.studioName || '-'}
        //           >{firstLineList?.studioName || '-'}</div>
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //           ></div>
        //         </li>
        //         {/* scenario */}
        //         <li
        //           className="dbg-studio-overall-finance-year-forecast-list-first-row-item"
        //           style={{
        //             width: 125 / totalWidth * 100 + '%',
        //             minWidth: '125px',
        //           }}
        //         >
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //             style={{
        //               justifyContent: 'flex-start',
        //               borderBottom: '1px solid #ddd',
        //             }}
        //             title="Base"
        //           >Base</div>
        //           <div
        //             className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //             style={{
        //               justifyContent: 'flex-start',
        //             }}
        //             title="Conservative"
        //           >Conservative</div>
        //         </li>
        //         {/* 年份列 */}
        //         {!loading && Array.isArray(years) && years?.map((t: string) => {
        //           return (
        //             <li
        //               key={t}
        //               className="dbg-studio-overall-finance-year-forecast-list-first-row-item"
        //               style={{
        //                 width: 130 / totalWidth * 100 + '%',
        //                 minWidth: '130px',
        //               }}
        //             >
        //               <div
        //                 className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //                 style={{
        //                   justifyContent: 'flex-end',
        //                   borderBottom: '1px solid #ddd',
        //                 }}
        //                 title={getFormatNumPlus(firstLineList?.[t + '_base']) || '-'}
        //               >{getFormatNumPlus(firstLineList?.[t + '_base']) || '-'}</div>
        //               <div
        //                 className="dbg-studio-overall-finance-year-forecast-list-first-row-item-td"
        //                 style={{
        //                   justifyContent: 'flex-end',
        //                 }}
        //                 title={getFormatNumPlus(firstLineList?.[t + '_conservative']) || '-'}
        //               >{getFormatNumPlus(firstLineList?.[t + '_conservative']) || '-'}</div>
        //             </li>
        //           );
        //         })}
        //       </ul>
        //     </div>
        //   </div>
        //   )
      }
    </CardSlot>
  );
}

BreakdownList.defaultProps = DEFAULT_PROPS;

export default memo(BreakdownList);
